// src/features/auth/components/LoginForm.jsx
import { useMemo, useState } from "react";

import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";

export default function LoginForm({
  appName,
  submitting = false,
  onSubmit,
  onForgotPassword,
  sx,
  errorMsg,
  onClearError,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");

  const canSubmit = useMemo(() => {
    return identifier.trim().length > 0 && password.length > 0 && !submitting;
  }, [identifier, password, submitting]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    if (typeof onSubmit !== "function") return;
    await onSubmit({ identifier, password });
  }

  const clearError = () => {
    if (errorMsg && typeof onClearError === "function") onClearError();
  };


  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        bgcolor: "rgba(191, 198, 225, 1)",
        borderRadius: "30px",
        p: "20px",
        boxShadow:
          "inset 0px 0px 4px rgba(117, 117, 138, 0.5), 0px 0px 10px rgba(165, 165, 178, 0.5)",
        ...(sx || {}),
      }}
    >
      <Stack spacing={3.75} alignItems="center">
        {/* Header */}
        <Stack spacing={2.5} alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              width: "60px",
              height: "60px",
              borderRadius: "42px",
              bgcolor: "rgba(14, 50, 149, 1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonIcon sx={{ width: "48px", height: "48px", color: "white" }} />
          </Box>

          <Box
            sx={{
              px: "20px",
              py: "10px",
              bgcolor: "rgba(14, 50, 149, 1)",
              borderRadius: "33px",
              boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Open Sans', Helvetica, Arial, sans-serif",
                fontSize: "22px",
                fontWeight: 600,
                color: "rgba(254, 254, 255, 1)",
              }}
            >
              Iniciar sesión
            </Typography>
          </Box>
        </Stack>

        {/* Body */}
        <Stack spacing={2.5} alignItems="center" sx={{ width: "100%" }}>
          <Typography
            sx={{
              fontFamily: "'Open Sans', Helvetica, Arial, sans-serif",
              fontSize: "18px",
              fontWeight: 600,
              color: "rgba(2, 8, 23, 1)",
            }}
          >
            Ingresá a {appName} con tu mail:
          </Typography>

          {errorMsg ? (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {errorMsg}
            </Alert>
          ) : null}

          <Stack spacing={1.25} sx={{ width: "100%" }}>
            <TextField
              value={identifier}
              onChange={(e) => {
                clearError();
                setIdentifier(e.target.value);
              }}
              placeholder="Mail"
              fullWidth
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ width: "24px", height: "24px" }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "rgba(247, 247, 248, 1)",
                  borderRadius: "10px",
                  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  fontFamily: "'Open Sans', Helvetica, Arial, sans-serif",
                  fontSize: "16px",
                  fontWeight: 300,
                  color: "rgba(2, 8, 23, 1)",
                },
              }}
              disabled={submitting}
            />

            <TextField
              value={password}
              onChange={(e) => {
                clearError();
                setPassword(e.target.value);
              }}
              type={showPassword ? "text" : "password"}
              placeholder="Contraseña"
              fullWidth
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ width: "24px", height: "24px" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        clearError();
                        setShowPassword((s) => !s);
                      }}
                      edge="end"
                      sx={{ width: "24px", height: "24px" }}
                      disabled={submitting}
                      aria-label={
                        showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                      }
                    >
                      {showPassword ? (
                        <VisibilityOffIcon sx={{ width: "24px", height: "24px" }} />
                      ) : (
                        <VisibilityIcon sx={{ width: "24px", height: "24px" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "rgba(247, 247, 248, 1)",
                  borderRadius: "10px",
                  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  fontFamily: "'Open Sans', Helvetica, Arial, sans-serif",
                  fontSize: "16px",
                  fontWeight: 300,
                  color: "rgba(2, 8, 23, 1)",
                },
              }}
              disabled={submitting}
            />

            <Typography
              sx={{
                fontFamily: "'Open Sans', Helvetica, Arial, sans-serif",
                fontSize: "15px",
                fontWeight: 400,
                color: "rgba(2, 8, 23, 1)",
                textAlign: "right",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={onForgotPassword}
            >
              Olvidé mi contraseña
            </Typography>
          </Stack>
        </Stack>

        {/* CTA */}
        <Button
          type="submit"
          disabled={!canSubmit}
          sx={{
            px: "20px",
            py: "10px",
            bgcolor: "rgba(18, 64, 191, 1)",
            borderRadius: "33px",
            "&:hover": { bgcolor: "rgba(14, 50, 149, 1)" },
            "&.Mui-disabled": {
              bgcolor: "rgba(18, 64, 191, 0.35)",
              color: "rgba(255,255,255,0.9)",
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Open Sans', Helvetica, Arial, sans-serif",
              fontSize: "16px",
              fontWeight: 700,
              color: "rgba(254, 254, 255, 1)",
              textTransform: "none",
            }}
          >
            {submitting ? "Ingresando..." : "Ingresar"}
          </Typography>
        </Button>
      </Stack>
    </Box>
  );
}
